import { makeAutoObservable, runInAction } from 'mobx';
import { createContext, useContext } from 'react';
import { SupportedUserRoles } from '@insurancemenu/imenu-components-lib/dist/types';
import { getAllEnrollmentPeriodsByGroupId } from 'api/operations/enrollmentService';
import { getEnrollsByUserId, getSubscriberWithChoices } from 'api/operations/electionService';
import { RouteV2Id } from 'routes/routesConfigV2';
import { getRootStoreInstance } from 'store/rootStore';
import { SubscriberMultiLoginModule } from 'store/modules/subscriber-multi-login/subscriberMultiLoginModule';
import { EmployeeInvitationStatusTypes } from 'types/electionService';
import { EnrollmentStage } from 'types/enrollmentService';

export class EmployerProfilePageStore {
  private _rootStore = getRootStoreInstance();

  public multiLoginModule = new SubscriberMultiLoginModule(this._rootStore);

  private _isGroupsListLoading = true;
  private _isSubscriberElected = false;

  public get isGroupsListLoading(): boolean {
    return this._isGroupsListLoading;
  }

  public get isInitialLoading(): boolean {
    return this._isGroupsListLoading;
  }

  public get isSubscriberElected(): boolean {
    return this._isSubscriberElected;
  }

  public setGroupsListLoading(isLoading: boolean): void {
    this._isGroupsListLoading = isLoading;
  }

  public async setupActiveGroup(groupIdFromUrl?: string): Promise<void> {
    this.setGroupsListLoading(true);
    await this.multiLoginModule.getGroupsList();
    await runInAction(async () => {
      await this.multiLoginModule.findActiveGroupAndRedirect(groupIdFromUrl);
      this.setGroupsListLoading(false);
    });
  }

  public setActiveGroup(activeGroupId: string): void {
    if (!this.multiLoginModule.setActiveGroupById(activeGroupId)) {
      throw new Error(`Group with id ${activeGroupId} not found`);
    }
    void this.checkSubscriberElectedStatus(activeGroupId);
    // Legacy stuff: loads group info for the sidebar.
    void this._rootStore.groupInfoController.requestInitialData(activeGroupId, false);
  }

  public async reLoginIntoActiveGroup(activeGroupId: string, subscriberRoleInGroup: SupportedUserRoles): Promise<void> {
    this.setGroupsListLoading(true);
    const routeToRedirect =
      subscriberRoleInGroup === 'EMPLOYEE' ? RouteV2Id.EMPLOYEE_PROFILE : RouteV2Id.EMPLOYER_PROFILE;
    await this.multiLoginModule.reLoginIntoActiveGroup(activeGroupId, routeToRedirect);
    runInAction(() => {
      this.setGroupsListLoading(false);
    });
  }

  private async checkSubscriberElectedStatus(groupId: string): Promise<void> {
    const [periods, enrollsWithSubs] = await Promise.all([
      getAllEnrollmentPeriodsByGroupId(groupId),
      getEnrollsByUserId(),
    ]);

    const activePeriods = periods.filter(
      (period) => period.hasConfirmedProposal && period.stage === EnrollmentStage.ENROLLING,
    );

    if (activePeriods.length > 1) {
      throw new Error('There can be only one active period');
    }

    const period = activePeriods[0];
    const subscriberId = period ? enrollsWithSubs?.[period.id] : undefined;

    if (!period || !subscriberId) {
      runInAction(() => {
        this._isSubscriberElected = false;
      });
      return;
    }

    const subscriberInfo = await getSubscriberWithChoices(subscriberId, period.id);
    runInAction(() => {
      this._isSubscriberElected = subscriberInfo?.selfEnrollStatus === EmployeeInvitationStatusTypes.ELECTED;
    });
  }

  constructor() {
    makeAutoObservable<EmployerProfilePageStore, '_rootStore'>(
      this,
      {
        _rootStore: false,
        multiLoginModule: false,
      },
      { autoBind: true },
    );
  }
}

export const EmployerProfilePageStoreContext = createContext<EmployerProfilePageStore | null>(null);

export const useEmployerProfilePageStore = (): EmployerProfilePageStore => {
  const context = useContext(EmployerProfilePageStoreContext);
  if (!context) {
    throw new Error('useEmployerProfilePageStore must be used within a EmployerProfilePageStoreContext');
  }
  return context;
};
